import { EditFileParams, EditFolderParams } from '@nextforce/api'
import { useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import {
  useFileDetails,
  useFolderDetails,
} from 'file_folder_repository/queries/details'
import { useEditFile, useEditFolder } from 'file_folder_repository/queries/edit'
import { EditFileFolderScreenProps } from 'home/stack/types'
import { isEmpty } from 'lodash'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import { errorAlert } from 'shared/alerts'
import { showHeaderButton } from 'shared/header/header_button'
import { showHeaderTitle } from 'shared/header/header_title'
import { EditFileFolderView } from './views'

export function EditFileFolderScreen({
  navigation,
  route,
}: EditFileFolderScreenProps) {
  const { id, type } = route.params
  const { theme } = useTheme()
  const folderDetailsQuery = useFolderDetails(id, type === 'folder')
  const fileDetailsQuery = useFileDetails(id, type === 'file')
  const editFileMutation = useEditFile()
  const editFolderMutation = useEditFolder()
  const defaultFields: EditFileParams | EditFolderParams = useMemo(() => {
    return {
      id,
      name:
        type === 'file'
          ? fileDetailsQuery.data?.name
          : folderDetailsQuery.data?.name,
      description:
        type === 'file'
          ? fileDetailsQuery.data?.description
          : folderDetailsQuery.data?.description,
      externalUrl:
        type === 'file'
          ? fileDetailsQuery.data?.currentVersion?.externalUrl
          : undefined,
    }
  }, [
    fileDetailsQuery.data?.currentVersion?.externalUrl,
    fileDetailsQuery.data?.description,
    fileDetailsQuery.data?.name,
    folderDetailsQuery.data?.description,
    folderDetailsQuery.data?.name,
    id,
    type,
  ])

  const [fields, setFields] = useState<EditFileParams & EditFolderParams>()
  const [loading, setLoading] = useState(false)

  const detailsQuery = type === 'file' ? fileDetailsQuery : folderDetailsQuery

  useEffect(() => {
    if (!fields) {
      setFields(defaultFields)
    }
  }, [defaultFields, fields])

  const onPressSave = useCallback(async () => {
    setLoading(true)
    try {
      if (type === 'file') {
        await editFileMutation.mutateAsync(fields as EditFileParams)
      } else {
        await editFolderMutation.mutateAsync(fields as EditFolderParams)
      }
      navigation.goBack()
    } catch (e) {
      errorAlert(e, `Error updating ${type}`)
    }
    setLoading(false)
  }, [editFileMutation, editFolderMutation, fields, navigation, type])

  const canSave = useCallback(() => {
    return (
      (!isEmpty(fields?.name) &&
        (fields?.name !== detailsQuery.data?.name ||
          fields?.description !== detailsQuery.data?.description)) ||
      (detailsQuery.data?.type === 'file' &&
        fields?.externalUrl !== detailsQuery.data?.currentVersion?.externalUrl)
    )
  }, [
    detailsQuery.data,
    fields?.description,
    fields?.externalUrl,
    fields?.name,
  ])

  useLayoutEffect(() => {
    showHeaderButton({
      navigation,
      theme,
      title: t('save'),
      onPress: onPressSave,
      disabled: !canSave(),
      loading: loading,
      position: 'right',
    })
  }, [canSave, loading, navigation, onPressSave, theme])

  useLayoutEffect(() => {
    showHeaderTitle(
      navigation,
      type === 'file' ? t('editFile') : t('editFolder')
    )
  }, [navigation, type])

  return (
    <EditFileFolderView
      data={detailsQuery.data}
      fields={fields}
      setFields={setFields}
    />
  )
}
